import { getNestedProperty } from "../../utils/nestedObjectHelper";

const isEmptyValue = (value) => {
  return value === "";
};

const fieldCondition = (value = "", config) => {
  if (config.isNotEmpty) {
    return !isEmptyValue(value);
  }

  if (config.isEmpty) {
    return isEmptyValue(value);
  }

  if (config.pattern) {
    const regExpPattern = RegExp(config.pattern, config.flags);

    return config.notMatch
      ? !regExpPattern.test(value)
      : regExpPattern.test(value);
  }

  if (typeof config.is === "function") {
    return config.is(value, config);
  }

  if (Object.prototype.hasOwnProperty.call(config, "greaterThan")) {
    return value > config.greaterThan;
  }

  if (Object.prototype.hasOwnProperty.call(config, "greaterThanOrEqualTo")) {
    return value >= config.greaterThanOrEqualTo;
  }

  if (Object.prototype.hasOwnProperty.call(config, "lessThan")) {
    return value < config.lessThan;
  }

  if (Object.prototype.hasOwnProperty.call(config, "lessThanOrEqualTo")) {
    return value <= config.lessThanOrEqualTo;
  }

  if (Object.prototype.hasOwnProperty.call(config, "isBoolean")) {
    return value === config.isBoolean;
  }

  if (Object.prototype.hasOwnProperty.call(config, "isFalse")) {
    return value === false;
  }

  if (value === null) {
    return false;
  }

  const isMatched = Array.isArray(config.is)
    ? !!config.is.includes(value)
    : value?.toString()?.toLowerCase() === config?.is?.toLowerCase();

  return config.notMatch ? !isMatched : isMatched;
};

const createResults = (condition) => ({
  positive: {
    visible: true,
    ...condition.then,
    result: true,
  },
  negative: {
    visible: false,
    ...condition.else,
    result: false,
  },
});

const allConditionsPass = (conditions, values, field) => {
  return !conditions
    .map((cond) => parseCondition(cond, values, field))
    .some(({ result }) => result === false);
};

const anyConditionPasses = (conditions, values, field) => {
  return conditions
    .map((cond) => parseCondition(cond, values, field))
    .some(({ result }) => result === true);
};

const handleSequenceCondition = (sequence, values, negativeResult) => {
  return sequence.reduce(
    (acc, curr) => {
      const result = parseCondition(curr, values);
      return {
        sets: [...acc.sets, ...(result.set ? [result.set] : [])],
        visible: acc.visible || result.visible,
        result: acc.result || result.result,
      };
    },
    { ...negativeResult, sets: [] }
  );
};

const resolveFieldName = (fieldName, field) => {
  return typeof fieldName === "function" ? fieldName(field) : fieldName;
};

const handleWhenString = (finalWhen, values, condition, results) => {
  const fieldValue = getNestedProperty(values, finalWhen);
  return fieldCondition(fieldValue, condition)
    ? results.positive
    : results.negative;
};

const handleWhenArray = (finalWhen, values, field, condition, results) => {
  const hasMatch = finalWhen
    .map((fieldName) => {
      const fieldValue = getNestedProperty(values, resolveFieldName(fieldName, field));
      return fieldCondition(fieldValue, condition);
    })
    .some((cond) => !!cond);

  return hasMatch ? results.positive : results.negative;
};

const handleArrayCondition = (condition, values, field, results) => {
  return allConditionsPass(condition, values, field) ? results.positive : results.negative;
};

const handleShowCondition = (condition, results) => {
  return condition?.show ? results.positive : results.negative;
};

const handleAndCondition = (condition, values, field, results) => {
  return allConditionsPass(condition.and, values, field) ? results.positive : results.negative;
};

const handleOrCondition = (condition, values, field, results) => {
  return anyConditionPasses(condition.or, values, field) ? results.positive : results.negative;
};

const handleNotCondition = (condition, values, results) => {
  return !parseCondition(condition.not, values).result ? results.positive : results.negative;
};

const handleWhenCondition = (condition, values, field, results) => {
  const finalWhen = resolveFieldName(condition.when, field);

  if (typeof finalWhen === "string") {
    return handleWhenString(finalWhen, values, condition, results);
  }

  if (Array.isArray(finalWhen)) {
    return handleWhenArray(finalWhen, values, field, condition, results);
  }

  return null;
};

const getConditionType = (condition) => {
  if (Array.isArray(condition)) return "array";
  if ("show" in condition) return "show";
  if (condition.and) return "and";
  if (condition.sequence) return "sequence";
  if (condition.or) return "or";
  if (condition.not) return "not";
  if (condition.when !== undefined) return "when";
  return "default";
};

export const parseCondition = (condition = [], values, field = null) => {
  const results = createResults(condition);
  const conditionType = getConditionType(condition);

  switch (conditionType) {
    case "array":
      return handleArrayCondition(condition, values, field, results);
    case "show":
      return handleShowCondition(condition, results);
    case "and":
      return handleAndCondition(condition, values, field, results);
    case "sequence":
      return handleSequenceCondition(condition.sequence, values, results.negative);
    case "or":
      return handleOrCondition(condition, values, field, results);
    case "not":
      return handleNotCondition(condition, values, results);
    case "when":
      return handleWhenCondition(condition, values, field, results) || results.negative;
    default:
      return results.negative;
  }
};
